import React from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import type { DigestRow } from "store/types/news";
import { formatNewsDate } from "utils/format-news-date";

type Props = {
  row: DigestRow | null;
  deliveryTime?: string;
  onCancel: () => void;
};

export const DigestHighlightsDialog: React.FC<Props> = ({ row, deliveryTime, onCancel }) => {
  const title = row ? `${formatNewsDate(row.date, { absoluteOnly: true })} — Daily Digest` : "Daily Digest";
  const subtitle = row && deliveryTime ? `${row.highlights.length} highlights · Delivered at ${deliveryTime}` : null;

  return (
    <Box
      sx={{
        width: "min(680px, 94vw)",
        maxHeight: "min(720px, 90vh)",
        display: "flex",
        flexDirection: "column" as const,
        gap: "20px",
        p: "32px",
        boxSizing: "border-box" as const
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "6px",
          pb: "8px",
          borderBottom: "1px solid #DBE8F5",
          flexShrink: 0
        }}
      >
        <Stack direction="column" gap="2px" sx={{ minWidth: 0 }}>
          <Typography
            component="div"
            sx={{ fontSize: "18px", fontWeight: 400, lineHeight: "24px", color: "#071F35" }}
          >
            {title}
          </Typography>
          {Boolean(subtitle) && (
            <Typography
              sx={{
                fontSize: 12,
                color: "#797979",
                mt: "2px"
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Stack>
        <IconButton aria-label="close" onClick={onCancel}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <Stack
        direction="column"
        gap="12px"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          mx: "-32px",
          px: "32px"
        }}
      >
        <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, display: "grid", gap: "4px" }}>
          {row?.highlights?.map((highlight, i) => (
            <Box
              component="li"
              key={i}
              sx={{
                fontSize: 13,
                color: "#071f35",
                pl: "13px",
                position: "relative",
                "&::before": {
                  // eslint-disable-next-line quotes
                  content: '""',
                  position: "absolute",
                  left: 0,
                  top: "10px",
                  width: "4px",
                  height: "4px",
                  borderRadius: "50%",
                  bgcolor: "#c09560"
                }
              }}
            >
              {highlight}
            </Box>
          ))}
        </Box>
        {Boolean(row?.summary) && (
          <Typography
            sx={{
              mt: "14px",
              pt: "12px",
              borderTop: "1px solid",
              borderColor: "divider",
              fontSize: 13,
              color: "#071f35"
            }}
          >
            {row?.summary}
          </Typography>
        )}
      </Stack>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        gap="8px"
        sx={{
          pt: "8px",
          borderTop: "1px solid #DBE8F5",
          flexShrink: 0
        }}
      >
        <Button variant="text" onClick={onCancel} sx={{ color: "secondary.main", textTransform: "none" }}>
          Close
        </Button>
      </Stack>
    </Box>
  );
};
